import { useCallback, useEffect, useState } from "react";
import { api, CATEGORIES } from "@/lib/api";
import { ModelCard } from "@/components/ModelCard";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, X } from "lucide-react";

export default function Explore() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [gender, setGender] = useState("all");
  const [trust, setTrust] = useState("all");
  const [bodyShape, setBodyShape] = useState("all");
  const [undertone, setUndertone] = useState("all");

  const fetchModels = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (appliedSearch) params.set("search", appliedSearch);
      if (category !== "all") params.set("category", category);
      if (gender !== "all") params.set("gender", gender);
      if (trust !== "all") params.set("trust", trust);
      if (bodyShape !== "all") params.set("body_shape", bodyShape);
      if (undertone !== "all") params.set("undertone", undertone);
      const { data } = await api.get(`/models?${params.toString()}`);
      setModels(data);
    } catch (e) {
      setModels([]);
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, category, gender, trust, bodyShape, undertone]);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setAppliedSearch(search.trim());
  };

  const clearFilters = () => {
    setSearch("");
    setAppliedSearch("");
    setCategory("all");
    setGender("all");
    setTrust("all");
    setBodyShape("all");
    setUndertone("all");
  };

  const hasFilters = search || appliedSearch || category !== "all" || gender !== "all" || trust !== "all" || bodyShape !== "all" || undertone !== "all";

  return (
    <div className="page-container py-10 sm:py-14">
      <div className="flex flex-col gap-2">
        <h1 className="font-display text-3xl tracking-[-0.01em] sm:text-4xl">Explore models</h1>
        <p className="text-sm text-muted-foreground">
          Search a look, rent it ethically. Every model here has passed a government ID scan and completed a
          full standardized AI dataset — and every booking needs their consent.
        </p>
      </div>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <form onSubmit={onSearchSubmit} className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="model-search-input"
            placeholder="Search by name, look, skills, hair, eyes…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-xl bg-card pl-9"
          />
        </form>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="model-filter-category-select" className="w-full rounded-xl bg-card sm:w-48">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {CATEGORIES.map((c) => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={gender} onValueChange={setGender}>
          <SelectTrigger data-testid="model-filter-gender-select" className="w-full rounded-xl bg-card sm:w-40">
            <SelectValue placeholder="Gender" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any gender</SelectItem>
            <SelectItem value="female">Female</SelectItem>
            <SelectItem value="male">Male</SelectItem>
            <SelectItem value="non-binary">Non-binary</SelectItem>
          </SelectContent>
        </Select>
        <Select value={trust} onValueChange={setTrust}>
          <SelectTrigger data-testid="model-filter-trust-select" className="w-full rounded-xl bg-card sm:w-52">
            <SelectValue placeholder="Trust level" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All trust levels</SelectItem>
            <SelectItem value="trusted">Verified or agency</SelectItem>
            <SelectItem value="verified">Verified only</SelectItem>
            <SelectItem value="agency">Agency-represented</SelectItem>
          </SelectContent>
        </Select>
        <Select value={bodyShape} onValueChange={setBodyShape}>
          <SelectTrigger data-testid="model-filter-bodyshape-select" className="w-full rounded-xl bg-card sm:w-44">
            <SelectValue placeholder="Body type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any body type</SelectItem>
            <SelectItem value="rectangle">Rectangle</SelectItem>
            <SelectItem value="hourglass">Hourglass</SelectItem>
            <SelectItem value="pear">Pear</SelectItem>
            <SelectItem value="inverted_triangle">Inverted triangle</SelectItem>
            <SelectItem value="athletic">Athletic</SelectItem>
            <SelectItem value="round">Round</SelectItem>
          </SelectContent>
        </Select>
        <Select value={undertone} onValueChange={setUndertone}>
          <SelectTrigger data-testid="model-filter-undertone-select" className="w-full rounded-xl bg-card sm:w-44">
            <SelectValue placeholder="Skin undertone" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any undertone</SelectItem>
            <SelectItem value="warm">Warm</SelectItem>
            <SelectItem value="cool">Cool</SelectItem>
            <SelectItem value="neutral">Neutral</SelectItem>
            <SelectItem value="olive">Olive</SelectItem>
          </SelectContent>
        </Select>
        {hasFilters && (
          <Button data-testid="model-filters-clear-button" variant="ghost" onClick={clearFilters} className="rounded-xl">
            <X className="mr-1 h-4 w-4" /> Clear
          </Button>
        )}
      </div>

      <p className="mt-6 text-xs uppercase tracking-wide text-muted-foreground">
        {loading ? "Searching…" : `${models.length} model${models.length === 1 ? "" : "s"} found`}
      </p>

      {loading ? (
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-6 lg:grid-cols-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="aspect-[3/4] rounded-xl" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ))}
        </div>
      ) : models.length === 0 ? (
        <div className="mt-12 rounded-xl border border-dashed p-12 text-center">
          <p className="font-display text-xl">No models match that look</p>
          <p className="mt-2 text-sm text-muted-foreground">Try a different search term or clear the filters.</p>
        </div>
      ) : (
        <div data-testid="model-results-grid" className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-6 lg:grid-cols-4">
          {models.map((m, i) => (
            <ModelCard key={m.id} model={m} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}
